import React, { UIEvent, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import invariant from "tiny-invariant";
import { addDaysToDate, calculateDaysBetweenDates, getDateIsoString } from "@lifeat/shared/util/DateUtil";
import VirtualListItem from "@common/VirtualListItem/VirtualListItem";
import { Signal, useComputed } from "@preact/signals-react";
import { ScrollbarStyles } from "@themes/ScrollbarStyles";
import TaskListMigrationIndicator from "../../../widget-workspace/components/Widget/TaskListWidget/TaskListMigrationIndicator";
import { isTodoListDataMigrationProcessing$ } from "../../TaskManagementGlobals";
import { ClientTaskListType, IdentifiableTask } from "../../TaskManagementTypes";
import { firstVisibleDate$, viewingDate$ } from "../PlannerGlobals";
import { generateDateRange } from "../PlannerUtil";
import { TASK_COL_WIDTH } from "./PlannerConstants";
import PlannerDateColumn from "./PlannerDateColumn";

const PlannerBoardContainer = styled.div<{ isVisible: boolean }>`
    display: ${(props) => (props.isVisible ? "flex" : "none")};
    background: ${(props) => props.theme.backgroundSecondary};
    height: 100%;
    width: 100%;
`;

const Viewport = styled.div`
    position: relative;
    ${ScrollbarStyles};
    overflow-x: scroll;
    overflow-y: hidden;
    white-space: nowrap;
    overflow-anchor: none;

    ::-webkit-scrollbar {
        height: 10px;
        background-color: ${(props) => props.theme.backgroundPrimary};
        border: 1px solid ${(props) => props.theme.borderPrimary};
    }

    ::-webkit-scrollbar-thumb {
        border: unset;
    }

    flex: 1;
`;

const ScrollArea = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    min-height: 100%;

    display: flex;
    flex-direction: row;
`;

const DAYS_FORWARD_PAGE_SIZE = 7;
const DAYS_BACKWARD_PAGE_SIZE = 7;
const SCROLL_WIDTH_OFFSET_TRIGGER = TASK_COL_WIDTH * 8;

export type PlannerBoardRef = {
    scrollToToday: () => void;
    scrollToDate: (date: Date) => void;
};

interface PlannerBoardProps {
    readonly taskList$: Signal<ClientTaskListType>;
    readonly isVisible: boolean;
}
const PlannerTaskBoard = forwardRef<PlannerBoardRef, PlannerBoardProps>((props, ref) => {
    const { taskList$ } = props;

    const viewportRef = useRef<HTMLDivElement | null>(null);

    const highestOrderRankTask$ = useComputed<IdentifiableTask | null>(() => {
        let highestRank = 0;
        let highestRankTask: IdentifiableTask | null = null;
        for (const [taskId, task] of Object.entries(taskList$.value)) {
            if (task.orderRank > highestRank) {
                highestRank = task.orderRank;
                highestRankTask = { id: taskId, document: task };
            }
        }
        return highestRankTask;
    });

    const [numVisibleDaysForward, setNumVisibleDaysForward] = useState(DAYS_FORWARD_PAGE_SIZE);
    const [numVisibleDaysBackward, setNumVisibleDaysBackward] = useState(DAYS_BACKWARD_PAGE_SIZE);
    const [hasScrolled, setHasScrolled] = useState(false);

    const dateTaskListMap$ = useComputed(() => {
        if (taskList$.value) {
            // Group tasks and key by date in YYYY-MM-DD format if the task has scheduleTime or scheduleDate set
            const newDateTaskListMap: { [dateStr: string]: ClientTaskListType } = {};
            for (const [taskId, task] of Object.entries(taskList$.value)) {
                if (task.schedulingDetails?.scheduleDate || task.schedulingDetails?.scheduleTime) {
                    const dateStr =
                        task.schedulingDetails.scheduleDate ??
                        getDateIsoString(new Date(task.schedulingDetails.scheduleTime));
                    invariant(dateStr, "dateStr should not be null");
                    if (!newDateTaskListMap[dateStr]) {
                        newDateTaskListMap[dateStr] = {};
                    }
                    const taskL = { ...newDateTaskListMap[dateStr] } ?? {};
                    taskL[taskId] = task;
                    newDateTaskListMap[dateStr] = taskL;
                }
            }
            return newDateTaskListMap;
        } else {
            return {};
        }
    });

    useImperativeHandle(ref, () => ({
        scrollToToday: () => {
            requestAnimationFrame(() => {
                viewportRef.current?.scrollTo({ left: numVisibleDaysBackward * TASK_COL_WIDTH, behavior: "smooth" });
            });
        },
        scrollToDate: (date: Date) => {
            if (viewportRef.current != null) {
                const isBefore = date < new Date();
                let dateDiff = calculateDaysBetweenDates(new Date(), date);
                if (isBefore) {
                    dateDiff *= -1;
                }
                requestAnimationFrame(() => {
                    const targetIndex = numVisibleDaysBackward + dateDiff;
                    viewportRef.current?.scrollTo({ left: targetIndex * TASK_COL_WIDTH, behavior: "smooth" });
                });
            }
        },
    }));

    const dateRange = useMemo(() => {
        return generateDateRange(viewingDate$.value, numVisibleDaysForward, numVisibleDaysBackward);
    }, [viewingDate$.value, numVisibleDaysForward, numVisibleDaysBackward]);

    const handleScroll = (event: UIEvent<HTMLDivElement>) => {
        const currentScrollOffset = event.currentTarget.scrollLeft;
        const clientWidth = event.currentTarget.clientWidth;

        // Get the first visible date
        const firstLoadedDate = dateRange[0];
        const firstVisibleDate = addDaysToDate(firstLoadedDate, currentScrollOffset / TASK_COL_WIDTH);
        firstVisibleDate$.value = firstVisibleDate;
        // Detect if scrolled to the far right
        const scrollWidth = TASK_COL_WIDTH * dateRange.length;
        if (currentScrollOffset >= scrollWidth - clientWidth - SCROLL_WIDTH_OFFSET_TRIGGER) {
            // Update firstVisibleDate to scroll to future dates
            setNumVisibleDaysForward(numVisibleDaysForward + DAYS_FORWARD_PAGE_SIZE);
        }

        // Detect if scrolled to the far left
        if (currentScrollOffset <= SCROLL_WIDTH_OFFSET_TRIGGER) {
            // Update firstVisibleDate to scroll to past dates
            setNumVisibleDaysBackward(numVisibleDaysBackward + DAYS_BACKWARD_PAGE_SIZE);
            // Keep the current scroll position, instead of snapping to the beginning when
            // the render paints.
            requestAnimationFrame(() => {
                viewportRef.current?.scrollBy({
                    left: DAYS_BACKWARD_PAGE_SIZE * TASK_COL_WIDTH,
                    behavior: "instant",
                });
            });
        }

        setHasScrolled(true);
    };

    // Set initial scroll position to the current date
    useEffect(() => {
        if (!hasScrolled && viewportRef.current != null) {
            viewportRef.current.scrollTo({ left: TASK_COL_WIDTH * numVisibleDaysBackward, behavior: "instant" });
        }
    });

    return (
        <PlannerBoardContainer isVisible={props.isVisible}>
            {!isTodoListDataMigrationProcessing$.value && (
                <Viewport ref={viewportRef} onScroll={handleScroll}>
                    <ScrollArea>
                        {dateRange.map((_, index) => (
                            <VirtualListItem
                                key={dateRange[index] ? getDateIsoString(dateRange[index]) : index}
                                viewportRef={viewportRef}
                                defaultWidth={TASK_COL_WIDTH}
                            >
                                <PlannerDateColumn
                                    index={index}
                                    data={{ highestOrderRankTask$, dateRange, dateTaskListMap$ }}
                                />
                            </VirtualListItem>
                        ))}
                    </ScrollArea>
                </Viewport>
            )}
            {isTodoListDataMigrationProcessing$.value && <TaskListMigrationIndicator />}
        </PlannerBoardContainer>
    );
});

export default PlannerTaskBoard;
